// DynamicCanvas.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import * as THREE from 'three';

// ============================================
// DEFAULT PARAMETERS
// ============================================
const DEFAULT_ZOOM = 1.13;
const DEFAULT_PARALLAX_STRENGTH = 0.09;
const DEFAULT_CAMERA_MOVE = 0.30;
const DEFAULT_BACKGROUND_SHIFT = 0.10;
const DEFAULT_GHOST_REDUCTION = 0.65;

// Depth processing defaults
const DEFAULT_DEPTH_SMOOTHING = 0.5;
const DEFAULT_EDGE_THRESHOLD = 0.05;
const DEFAULT_DEPTH_CONTRAST = 1.2;
const DEFAULT_BLACK_POINT = 0.0;
const DEFAULT_WHITE_POINT = 0.9;
const DEFAULT_MIDTONE_SHIFT = -0.4;
const DEFAULT_DEPTH_GAMMA = 1.0;
const DEFAULT_EDGE_ENHANCE = 0.3;
const DEFAULT_CORNER_BOOST = 0.2;
const DEFAULT_CYLINDER_STRENGTH = 0.0;
const DEFAULT_CYLINDER_EDGE_CLAMP = 0.5;
const DEFAULT_CYLINDER_GAMMA = 1.0;
const DEFAULT_CYLINDER_WIDTH = 1.0;
const DEFAULT_CYLINDER_HEIGHT = 1.0;
const DEFAULT_CORNER_PLUS_STRENGTH = 0.0;
const DEFAULT_CORNER_PLUS_GAMMA = 1.0;
const DEFAULT_LOCAL_CONTRAST = 0.15;
const DEFAULT_MIDTONE_SPREAD = 0.25;
const DEFAULT_MORPH_RADIUS = 2;
const DEFAULT_RADIAL_FALLOFF = 0.0;
const DEFAULT_NOISE_REDUCTION = 0.2;
const DEFAULT_DETAIL_PRESERVE = 0.4;

// Sensitivity defaults
const DEFAULT_MOUSE_SENSITIVITY_X = 1.0;
const DEFAULT_MOUSE_SENSITIVITY_Y = 1.0;
const DEFAULT_GYRO_SENSITIVITY_X = 1.0;
const DEFAULT_GYRO_SENSITIVITY_Y = 1.0;

// ============================================
// DEPTH PROCESSING UTILITIES
// ============================================

const bilateralFilter = (depthData, width, height, spatialSigma, rangeSigma) => {
  const result = new Float32Array(width * height);
  const kernelRadius = Math.ceil(spatialSigma * 2.5);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const centerIdx = y * width + x;
      const centerVal = depthData[centerIdx];
      let sum = 0, weightSum = 0;
      for (let dy = -kernelRadius; dy <= kernelRadius; dy++) {
        for (let dx = -kernelRadius; dx <= kernelRadius; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny < 0 || ny >= height || nx < 0 || nx >= width) continue;
          const nIdx = ny * width + nx;
          const nVal = depthData[nIdx];
          const spatialDist = Math.sqrt(dx*dx + dy*dy);
          const rangeDist = Math.abs(centerVal - nVal);
          const spatialWeight = Math.exp(-(spatialDist * spatialDist) / (2 * spatialSigma * spatialSigma));
          const rangeWeight = Math.exp(-(rangeDist * rangeDist) / (2 * rangeSigma * rangeSigma));
          const weight = spatialWeight * rangeWeight;
          sum += nVal * weight;
          weightSum += weight;
        }
      }
      result[centerIdx] = sum / weightSum;
    }
  }
  return result;
};

const unsharpMask = (depthData, width, height, strength, threshold) => {
  const blurred = bilateralFilter(depthData, width, height, 1.5, 0.1);
  const result = new Float32Array(width * height);
  for (let i = 0; i < depthData.length; i++) {
    const diff = depthData[i] - blurred[i];
    if (Math.abs(diff) > threshold) {
      result[i] = Math.max(0, Math.min(1, depthData[i] + diff * strength));
    } else {
      result[i] = depthData[i];
    }
  }
  return result;
};

const applyCornerBoost = (depthData, width, height, strength) => {
  const result = new Float32Array(width * height);
  const cx = width / 2, cy = height / 2;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const dx = (x - cx) / cx;
      const dy = (y - cy) / cy;
      const dist = Math.sqrt(dx*dx + dy*dy) / Math.sqrt(2);
      const cornerWeight = Math.min(1, dist * 1.5);
      const boost = 1.0 + strength * cornerWeight;
      result[idx] = Math.min(1, depthData[idx] * boost);
    }
  }
  return result;
};

const applyCylinderCurvature = (depthData, width, height, strength, edgeClamp, gamma, widthFactor, heightFactor) => {
  const result = new Float32Array(width * height);
  const halfW = width / 2;
  const halfH = height / 2;
  const effectiveW = halfW * widthFactor;
  const effectiveH = halfH * heightFactor;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const nx = Math.min(1, Math.abs(x - halfW) / effectiveW);
      const ny = Math.min(1, Math.abs(y - halfH) / effectiveH);
      const dist = Math.sqrt(nx*nx + ny*ny * 0.3);
      const clampedDist = Math.pow(dist, edgeClamp * 2 + 0.1);
      const factor = 1.0 - clampedDist * clampedDist;
      let modulation = 1.0 + strength * (factor - 0.5);
      if (gamma !== 1.0) {
        modulation = Math.pow(modulation, gamma);
      }
      result[idx] = Math.max(0, Math.min(1, depthData[idx] * modulation));
    }
  }
  return result;
};

const applyCornerPlusEmphasis = (depthData, width, height, strength, gamma) => {
  const result = new Float32Array(width * height);
  const cx = width / 2, cy = height / 2;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const dx = Math.abs(x - cx) / cx;
      const dy = Math.abs(y - cy) / cy;
      const cornerWeight = dx * dy;
      let modulation = 1.0 + strength * cornerWeight;
      if (gamma !== 1.0) {
        modulation = Math.pow(modulation, gamma);
      }
      result[idx] = Math.max(0, Math.min(1, depthData[idx] * modulation));
    }
  }
  return result;
};

const applyLocalContrast = (depthData, width, height, strength, tileSize = 32) => {
  const result = new Float32Array(width * height);
  const halfTile = tileSize / 2;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      let sum = 0, count = 0;
      const startY = Math.max(0, y - halfTile);
      const endY = Math.min(height, y + halfTile);
      const startX = Math.max(0, x - halfTile);
      const endX = Math.min(width, x + halfTile);
      for (let ny = startY; ny < endY; ny++) {
        for (let nx = startX; nx < endX; nx++) {
          sum += depthData[ny * width + nx];
          count++;
        }
      }
      const localMean = sum / count;
      const diff = depthData[idx] - localMean;
      result[idx] = Math.max(0, Math.min(1, localMean + diff * (1 + strength)));
    }
  }
  return result;
};

const applyMidtoneSpread = (depthData, strength) => {
  const result = new Float32Array(depthData.length);
  const center = 0.5;
  for (let i = 0; i < depthData.length; i++) {
    let val = depthData[i];
    const diff = val - center;
    val = center + diff * (1 + strength * (1 - Math.abs(diff) * 2));
    result[i] = Math.max(0, Math.min(1, val));
  }
  return result;
};

const morphologicalClosing = (depthData, width, height, radius) => {
  const dilated = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let maxVal = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
            maxVal = Math.max(maxVal, depthData[ny * width + nx]);
          }
        }
      }
      dilated[y * width + x] = maxVal;
    }
  }
  const closed = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let minVal = 1;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
            minVal = Math.min(minVal, dilated[ny * width + nx]);
          }
        }
      }
      closed[y * width + x] = minVal;
    }
  }
  return closed;
};

const applyRadialFalloff = (depthData, width, height, strength) => {
  const result = new Float32Array(width * height);
  const cx = width / 2, cy = height / 2;
  const maxDist = Math.sqrt(cx*cx + cy*cy);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const dx = x - cx, dy = y - cy;
      const dist = Math.sqrt(dx*dx + dy*dy) / maxDist;
      const weight = 1.0 - dist * strength;
      result[idx] = depthData[idx] * Math.max(0.5, weight);
    }
  }
  return result;
};

const applyDetailPreserve = (depthData, width, height, strength) => {
  const grad = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const gx = depthData[idx + 1] - depthData[idx - 1];
      const gy = depthData[idx + width] - depthData[idx - width];
      grad[idx] = Math.sqrt(gx*gx + gy*gy);
    }
  }
  let maxGrad = 0;
  for (let i = 0; i < grad.length; i++) maxGrad = Math.max(maxGrad, grad[i]);
  if (maxGrad > 0) {
    for (let i = 0; i < grad.length; i++) grad[i] /= maxGrad;
  }
  const result = new Float32Array(width * height);
  for (let i = 0; i < depthData.length; i++) {
    const edgeFactor = grad[i];
    result[i] = depthData[i] * (1 + strength * edgeFactor);
    result[i] = Math.max(0, Math.min(1, result[i]));
  }
  return result;
};

const adjustDepthLevels = (depthData, contrast, blackPoint, whitePoint, midtoneShift, gamma) => {
  const result = new Float32Array(depthData.length);
  const bp = Math.max(0, Math.min(1, blackPoint));
  const wp = Math.max(bp + 0.01, Math.min(1, whitePoint));
  const range = wp - bp;
  for (let i = 0; i < depthData.length; i++) {
    let val = (depthData[i] - bp) / range;
    val = Math.max(0, Math.min(1, val));
    val = 0.5 + (val - 0.5) * contrast;
    if (midtoneShift !== 0) {
      const g = 1.0 + midtoneShift * 2.0;
      val = Math.pow(val, g);
    }
    if (gamma !== 1.0) {
      val = Math.pow(val, gamma);
    }
    result[i] = Math.max(0, Math.min(1, val));
  }
  return result;
};

const applyDepthProcessing = (depthData, width, height, params) => {
  let processed = depthData;
  processed = bilateralFilter(processed, width, height, params.depthSmoothing, params.edgeThreshold);
  if (params.noiseReduction > 0) {
    processed = bilateralFilter(processed, width, height, params.noiseReduction * 2, params.edgeThreshold);
  }
  if (params.cornerBoost > 0) {
    processed = applyCornerBoost(processed, width, height, params.cornerBoost);
  }
  if (params.cylinderStrength !== 0) {
    processed = applyCylinderCurvature(
      processed, width, height,
      params.cylinderStrength,
      params.cylinderEdgeClamp,
      params.cylinderGamma,
      params.cylinderWidth,
      params.cylinderHeight
    );
  }
  if (params.cornerPlusStrength !== 0) {
    processed = applyCornerPlusEmphasis(
      processed, width, height,
      params.cornerPlusStrength,
      params.cornerPlusGamma
    );
  }
  if (params.localContrast > 0) {
    processed = applyLocalContrast(processed, width, height, params.localContrast);
  }
  if (params.midtoneSpread > 0) {
    processed = applyMidtoneSpread(processed, params.midtoneSpread);
  }
  if (params.radialFalloff > 0) {
    processed = applyRadialFalloff(processed, width, height, params.radialFalloff);
  }
  if (params.detailPreserve > 0) {
    processed = applyDetailPreserve(processed, width, height, params.detailPreserve);
  }
  processed = adjustDepthLevels(
    processed,
    params.depthContrast,
    params.blackPoint,
    params.whitePoint,
    params.midtoneShift,
    params.depthGamma
  );
  if (params.edgeEnhance > 0) {
    processed = unsharpMask(processed, width, height, params.edgeEnhance, params.edgeThreshold);
  }
  if (params.morphRadius > 0) {
    processed = morphologicalClosing(processed, width, height, Math.floor(params.morphRadius));
  }
  return processed;
};

const generateHeuristicDepthMap = async (imageElement, params) => {
  const maxDim = 800;
  let w = imageElement.width;
  let h = imageElement.height;
  if (w > maxDim || h > maxDim) {
    const ratio = Math.min(maxDim / w, maxDim / h);
    w = Math.floor(w * ratio);
    h = Math.floor(h * ratio);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(imageElement, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const depthValues = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const maxC = Math.max(r, g, b);
      const minC = Math.min(r, g, b);
      const sat = maxC === 0 ? 0 : (maxC - minC) / maxC;
      const yBias = y / h;
      let depth = lum * 0.4 + (1 - sat) * 0.3 + yBias * 0.3;
      depthValues[y * w + x] = Math.min(1, Math.max(0, depth));
    }
  }

  let processed = applyDepthProcessing(depthValues, w, h, params);

  const origW = imageElement.width;
  const origH = imageElement.height;
  const finalDepth = new Float32Array(origW * origH);
  const scaleX = w / origW;
  const scaleY = h / origH;
  for (let y = 0; y < origH; y++) {
    for (let x = 0; x < origW; x++) {
      const srcX = x * scaleX;
      const srcY = y * scaleY;
      const x1 = Math.floor(srcX);
      const y1 = Math.floor(srcY);
      const x2 = Math.min(x1 + 1, w - 1);
      const y2 = Math.min(y1 + 1, h - 1);
      const fx = srcX - x1;
      const fy = srcY - y1;
      const d11 = processed[y1 * w + x1];
      const d21 = processed[y1 * w + x2];
      const d12 = processed[y2 * w + x1];
      const d22 = processed[y2 * w + x2];
      finalDepth[y * origW + x] = d11 * (1 - fx) * (1 - fy) + d21 * fx * (1 - fy) + d12 * (1 - fx) * fy + d22 * fx * fy;
    }
  }

  const depthCanvas = document.createElement('canvas');
  depthCanvas.width = origW;
  depthCanvas.height = origH;
  const depthCtx = depthCanvas.getContext('2d');
  const depthImgData = depthCtx.createImageData(origW, origH);
  for (let i = 0; i < finalDepth.length; i++) {
    const val = Math.round(finalDepth[i] * 255);
    const idx = i * 4;
    depthImgData.data[idx] = val;
    depthImgData.data[idx + 1] = val;
    depthImgData.data[idx + 2] = val;
    depthImgData.data[idx + 3] = 255;
  }
  depthCtx.putImageData(depthImgData, 0, 0);

  return {
    depthMapUrl: depthCanvas.toDataURL('image/png'),
    depthData: finalDepth,
    width: origW,
    height: origH,
  };
};

const loadImageElement = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

// ============================================
// SHADER PARALLAX (Internal Component)
// ============================================
const ShaderParallax = ({ 
  imageUrl, depthUrl, backgroundUrl, isActive, gyroEnabled,
  zoom, parallaxStrength, cameraMoveAmount, backgroundShift, ghostReduction,
  mouseSensitivityX, mouseSensitivityY, gyroSensitivityX, gyroSensitivityY,
}) => {
  const { camera, size } = useThree();
  const meshRef = useRef();
  const bgMeshRef = useRef();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [gyroPos, setGyroPos] = useState({ x: 0, y: 0 });
  const [progress, setProgress] = useState(0);
  const startTime = useRef(0);

  const imageTex = useTexture(imageUrl || '');
  const depthTex = useTexture(depthUrl || '');
  const bgTex = useTexture(backgroundUrl || '');

  useEffect(() => {
    if (imageTex) {
      imageTex.minFilter = THREE.LinearFilter;
      imageTex.magFilter = THREE.LinearFilter;
    }
    if (depthTex) {
      depthTex.minFilter = THREE.LinearFilter;
      depthTex.magFilter = THREE.LinearFilter;
    }
    if (bgTex) {
      bgTex.minFilter = THREE.LinearFilter;
      bgTex.magFilter = THREE.LinearFilter;
    }
  }, [imageTex, depthTex, bgTex]);

  useEffect(() => {
    const handleMouse = (e) => {
      const wrapper = document.querySelector('.canvas-artboard, .desktop-editor-canvas-inner');
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      setMousePos({ x, y });
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  useEffect(() => {
    if (!gyroEnabled) return;
    const handleOrientation = (e) => {
      if (e.beta === null || e.gamma === null) return;
      const beta = Math.max(-20, Math.min(20, e.beta)) / 20;
      const gamma = Math.max(-20, Math.min(20, e.gamma)) / 20;
      setGyroPos({ x: gamma, y: beta });
    };
    window.addEventListener('deviceorientation', handleOrientation);
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, [gyroEnabled]);

  useEffect(() => {
    if (isActive) {
      startTime.current = performance.now();
      setProgress(0);
    }
  }, [isActive]);

  const material = useMemo(() => {
    if (!imageTex || !depthTex) return null;
    return new THREE.ShaderMaterial({
      uniforms: {
        u_image: { value: imageTex },
        u_depth: { value: depthTex },
        u_mouse: { value: new THREE.Vector2(0, 0) },
        u_gyro: { value: new THREE.Vector2(0, 0) },
        u_strength: { value: parallaxStrength },
        u_progress: { value: 0 },
        u_aspect: { value: 1 },
        u_imageAspect: { value: 1 },
        u_planeAspect: { value: 1 },
        u_useGyro: { value: gyroEnabled ? 1.0 : 0.0 },
        u_ghostReduction: { value: ghostReduction },
        u_mouseSens: { value: new THREE.Vector2(mouseSensitivityX, mouseSensitivityY) },
        u_gyroSens: { value: new THREE.Vector2(gyroSensitivityX, gyroSensitivityY) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform sampler2D u_image;
        uniform sampler2D u_depth;
        uniform vec2 u_mouse;
        uniform vec2 u_gyro;
        uniform float u_strength;
        uniform float u_progress;
        uniform float u_aspect;
        uniform float u_imageAspect;
        uniform float u_planeAspect;
        uniform float u_useGyro;
        uniform float u_ghostReduction;
        uniform vec2 u_mouseSens;
        uniform vec2 u_gyroSens;
        varying vec2 vUv;

        void main() {
          float scale = max(u_planeAspect / u_imageAspect, 1.0);
          vec2 coverUv = (vUv - 0.5) * scale + 0.5;
          coverUv = clamp(coverUv, 0.0, 1.0);

          float depth = texture2D(u_depth, coverUv).r;
          float sharpDepth = pow(depth, 1.0 + u_ghostReduction * 2.5);

          vec2 inputOffset = u_useGyro > 0.5 ? u_gyro * u_gyroSens : u_mouse * u_mouseSens;
          float depthFactor = sharpDepth * sharpDepth;
          vec2 center = vec2(0.5) + inputOffset * 0.15 * u_progress;
          vec2 delta = coverUv - center;
          delta.x *= u_aspect;

          float displace = u_strength * depthFactor * u_progress;
          vec2 offset = delta * displace;

          vec2 displacedUv = coverUv + offset;
          vec2 clampedUv = clamp(displacedUv, 0.005, 0.995);

          vec4 color = texture2D(u_image, clampedUv);
          gl_FragColor = color;
        }
      `,
      transparent: false,
    });
  }, [imageTex, depthTex, gyroEnabled, parallaxStrength, ghostReduction, mouseSensitivityX, mouseSensitivityY, gyroSensitivityX, gyroSensitivityY]);

  const bgMaterial = useMemo(() => {
    if (!bgTex) return null;
    return new THREE.MeshBasicMaterial({ map: bgTex, toneMapped: false });
  }, [bgTex]);

  useFrame(() => {
    if (!material) return;

    if (isActive) {
      const elapsed = (performance.now() - startTime.current) / 1000;
      const p = Math.min(elapsed / 1.2, 1.0);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      setProgress(eased);
      material.uniforms.u_progress.value = eased;
    }

    const inputX = gyroEnabled ? gyroPos.x * gyroSensitivityX : mousePos.x * mouseSensitivityX;
    const inputY = gyroEnabled ? gyroPos.y * gyroSensitivityY : mousePos.y * mouseSensitivityY;

    material.uniforms.u_mouse.value.set(mousePos.x, mousePos.y);
    material.uniforms.u_gyro.value.set(gyroPos.x, gyroPos.y);
    material.uniforms.u_useGyro.value = gyroEnabled ? 1.0 : 0.0;
    material.uniforms.u_ghostReduction.value = ghostReduction;

    if (imageTex?.image) {
      const imgAspect = imageTex.image.width / imageTex.image.height;
      material.uniforms.u_imageAspect.value = imgAspect;
      material.uniforms.u_aspect.value = size.width / size.height;
    }

    camera.position.x = inputX * cameraMoveAmount * progress;
    camera.position.y = inputY * cameraMoveAmount * progress;
    camera.position.z = 5;
    camera.lookAt(0, 0, 0);

    if (bgMeshRef.current && bgMaterial) {
      bgMeshRef.current.position.x = -inputX * backgroundShift * progress;
      bgMeshRef.current.position.y = -inputY * backgroundShift * progress;
    }
  });

  const distance = 5;
  const vFOV = (camera.fov * Math.PI) / 180;
  const planeHeight = 2 * Math.tan(vFOV / 2) * distance * zoom;
  const planeWidth = planeHeight * (size.width / size.height);

  if (material) {
    material.uniforms.u_planeAspect.value = planeWidth / planeHeight;
  }

  if (!material || !bgMaterial) return null;

  return (
    <>
      <mesh ref={bgMeshRef} position={[0, 0, -1.0]} material={bgMaterial}>
        <planeGeometry args={[planeWidth, planeHeight]} />
      </mesh>
      <mesh ref={meshRef} material={material} position={[0, 0, 0]}>
        <planeGeometry args={[planeWidth, planeHeight]} />
      </mesh>
    </>
  );
};

// ============================================
// SLIDER COMPONENT (Internal)
// ============================================
const Slider = ({ label, value, min, max, step, onChange }) => (
  <div className="slider-group">
    <label>{label}: {typeof value === 'number' ? value.toFixed(3) : value}</label>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))} />
  </div>
);

// ============================================
// MAIN DYNAMIC CANVAS COMPONENT
// ============================================
const DynamicCanvas = ({ 
  imageUrl, 
  isActive, 
  onActivate, 
  onDeactivate,
  gyroEnabled,
  onRequestGyro,
  onGyroToggle,
  isModal = false,
  className = ""
}) => {
  // If not active, don't render anything
  if (!isActive) return null;

  // Settings UI state
  const [showSettings, setShowSettings] = useState(true);
  const [depthMap, setDepthMap] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [stage, setStage] = useState('generate');
  const [activeTab, setActiveTab] = useState('depth');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCylinderSub, setShowCylinderSub] = useState(false);
  const [showCornerPlusSub, setShowCornerPlusSub] = useState(false);
  
  const [depthParams, setDepthParams] = useState({
    depthSmoothing: DEFAULT_DEPTH_SMOOTHING,
    edgeThreshold: DEFAULT_EDGE_THRESHOLD,
    depthContrast: DEFAULT_DEPTH_CONTRAST,
    blackPoint: DEFAULT_BLACK_POINT,
    whitePoint: DEFAULT_WHITE_POINT,
    midtoneShift: DEFAULT_MIDTONE_SHIFT,
    depthGamma: DEFAULT_DEPTH_GAMMA,
    edgeEnhance: DEFAULT_EDGE_ENHANCE,
    cornerBoost: DEFAULT_CORNER_BOOST,
    cylinderStrength: DEFAULT_CYLINDER_STRENGTH,
    cylinderEdgeClamp: DEFAULT_CYLINDER_EDGE_CLAMP,
    cylinderGamma: DEFAULT_CYLINDER_GAMMA,
    cylinderWidth: DEFAULT_CYLINDER_WIDTH,
    cylinderHeight: DEFAULT_CYLINDER_HEIGHT,
    cornerPlusStrength: DEFAULT_CORNER_PLUS_STRENGTH,
    cornerPlusGamma: DEFAULT_CORNER_PLUS_GAMMA,
    localContrast: DEFAULT_LOCAL_CONTRAST,
    midtoneSpread: DEFAULT_MIDTONE_SPREAD,
    morphRadius: DEFAULT_MORPH_RADIUS,
    radialFalloff: DEFAULT_RADIAL_FALLOFF,
    noiseReduction: DEFAULT_NOISE_REDUCTION,
    detailPreserve: DEFAULT_DETAIL_PRESERVE,
  });

  const [liveParams, setLiveParams] = useState({
    zoom: DEFAULT_ZOOM,
    parallaxStrength: DEFAULT_PARALLAX_STRENGTH,
    cameraMove: DEFAULT_CAMERA_MOVE,
    backgroundShift: DEFAULT_BACKGROUND_SHIFT,
    ghostReduction: DEFAULT_GHOST_REDUCTION,
    mouseSensitivityX: DEFAULT_MOUSE_SENSITIVITY_X,
    mouseSensitivityY: DEFAULT_MOUSE_SENSITIVITY_Y,
    gyroSensitivityX: DEFAULT_GYRO_SENSITIVITY_X,
    gyroSensitivityY: DEFAULT_GYRO_SENSITIVITY_Y,
  });

  const generateDepthMap = async () => {
    setIsProcessing(true);
    setProcessingStatus('Generating depth map...');
    try {
      const img = await loadImageElement(imageUrl);
      const result = await generateHeuristicDepthMap(img, depthParams);
      setDepthMap(result.depthMapUrl);
      
      const bgCanvas = document.createElement('canvas');
      bgCanvas.width = img.width;
      bgCanvas.height = img.height;
      const bgCtx = bgCanvas.getContext('2d');
      bgCtx.filter = 'blur(20px)';
      bgCtx.drawImage(img, 0, 0);
      setBackgroundUrl(bgCanvas.toDataURL());
      
      setProcessingStatus('');
      setIsProcessing(false);
      setStage('tuning');
    } catch (error) {
      console.error('Depth generation error:', error);
      setProcessingStatus('Error generating depth');
      setIsProcessing(false);
    }
  };

  const handleApply = () => {
    setShowSettings(false);
    if (onActivate) onActivate();
    if (onRequestGyro && gyroEnabled === false) {
      onRequestGyro();
    }
  };

  // If active and we have depth data, show the 3D viewer
  if (isActive && depthMap && !showSettings) {
    const viewerContent = (
      <>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onDeactivate) onDeactivate();
          }}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 100,
            background: 'rgba(0,0,0,0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ✕
        </button>
        <Canvas
          camera={{ position: [0, 0, 5], fov: 45 }}
          style={{
            width: '100%',
            height: '100%',
            minHeight: '400px',
          }}
          gl={{ preserveDrawingBuffer: true, antialias: true }}
          onCreated={({ gl }) => gl.setClearColor('#1a1a1a', 1)}
        >
          <ambientLight intensity={1.5} />
          <ShaderParallax
            imageUrl={imageUrl}
            depthUrl={depthMap}
            backgroundUrl={backgroundUrl}
            isActive={isActive}
            gyroEnabled={gyroEnabled}
            zoom={liveParams.zoom}
            parallaxStrength={liveParams.parallaxStrength}
            cameraMoveAmount={liveParams.cameraMove}
            backgroundShift={liveParams.backgroundShift}
            ghostReduction={liveParams.ghostReduction}
            mouseSensitivityX={liveParams.mouseSensitivityX}
            mouseSensitivityY={liveParams.mouseSensitivityY}
            gyroSensitivityX={liveParams.gyroSensitivityX}
            gyroSensitivityY={liveParams.gyroSensitivityY}
          />
        </Canvas>
      </>
    );

    if (isModal) {
      return (
        <div className="dynamic-canvas-modal-overlay" onClick={onDeactivate}>
          <div className="dynamic-canvas-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="dynamic-canvas-viewer" style={{ position: 'relative', width: '100%', height: '100%' }}>
              {viewerContent}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={`dynamic-canvas-viewer ${className}`} style={{ position: 'relative', width: '100%', height: '100%' }}>
        {viewerContent}
      </div>
    );
  }

  // Otherwise show the settings UI
  const settingsContent = (
    <div className={`dynamic-canvas-settings ${className}`}>
      <div className="dynamic-canvas-settings-header">
        <button 
          className="close-button" 
          onClick={(e) => {
            e.stopPropagation();
            if (onDeactivate) {
              onDeactivate();
            }
          }}
        >
          ✕
        </button>
        <h2>Dynamic Canvas</h2>
      </div>
      
      <div className="dynamic-canvas-settings-content">
        {stage === 'generate' && (
          <div className="generate-section">
            <div className="preview-container">
              <img src={imageUrl} alt="Preview" className="preview-image" crossOrigin="anonymous" />
            </div>
            <button 
              className="generate-button"
              onClick={generateDepthMap}
              disabled={isProcessing}
            >
              {isProcessing ? processingStatus : 'Generate Depth Map'}
            </button>
          </div>
        )}
        
        {stage === 'tuning' && depthMap && (
          <div className="tuning-section">
            <div className="preview-dual">
              <img src={imageUrl} alt="Original" crossOrigin="anonymous" />
              <img src={depthMap} alt="Depth Map" />
            </div>
            
            <div className="tab-bar">
              <button 
                className={`tab-button ${activeTab === 'depth' ? 'active' : ''}`}
                onClick={() => setActiveTab('depth')}
              >
                Depth Tuning
              </button>
              <button 
                className={`tab-button ${activeTab === 'live' ? 'active' : ''}`}
                onClick={() => setActiveTab('live')}
              >
                Live Controls
              </button>
            </div>
            
            {activeTab === 'depth' && (
              <div className="depth-tuning-panel">
                <h3>Depth Map Tuning</h3>
                
                <Slider label="Smoothing" value={depthParams.depthSmoothing} min={0.1} max={3.0} step={0.1}
                  onChange={(v) => setDepthParams(p => ({ ...p, depthSmoothing: v }))} />
                <Slider label="Edge Threshold" value={depthParams.edgeThreshold} min={0.01} max={0.30} step={0.01}
                  onChange={(v) => setDepthParams(p => ({ ...p, edgeThreshold: v }))} />
                <Slider label="Contrast" value={depthParams.depthContrast} min={0.5} max={2.5} step={0.1}
                  onChange={(v) => setDepthParams(p => ({ ...p, depthContrast: v }))} />
                <Slider label="Black Point" value={depthParams.blackPoint} min={0.0} max={0.5} step={0.01}
                  onChange={(v) => setDepthParams(p => ({ ...p, blackPoint: v }))} />
                <Slider label="White Point" value={depthParams.whitePoint} min={0.5} max={1.0} step={0.01}
                  onChange={(v) => setDepthParams(p => ({ ...p, whitePoint: v }))} />
                <Slider label="Midtone Shift" value={depthParams.midtoneShift} min={-0.8} max={0.8} step={0.05}
                  onChange={(v) => setDepthParams(p => ({ ...p, midtoneShift: v }))} />
                <Slider label="Depth Gamma" value={depthParams.depthGamma} min={0.5} max={2.0} step={0.05}
                  onChange={(v) => setDepthParams(p => ({ ...p, depthGamma: v }))} />
                
                <button className="advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
                  {showAdvanced ? '▼ Hide Advanced' : '▶ Show Advanced Controls'}
                </button>
                
                {showAdvanced && (
                  <>
                    <Slider label="Edge Enhancement" value={depthParams.edgeEnhance} min={0.0} max={1.0} step={0.05}
                      onChange={(v) => setDepthParams(p => ({ ...p, edgeEnhance: v }))} />
                    <Slider label="Corner Boost" value={depthParams.cornerBoost} min={0.0} max={0.5} step={0.02}
                      onChange={(v) => setDepthParams(p => ({ ...p, cornerBoost: v }))} />
                    
                    <div style={{ marginTop: 12, marginBottom: 4 }}>
                      <Slider label="Cylinder Curvature" value={depthParams.cylinderStrength} min={-0.5} max={0.5} step={0.01}
                        onChange={(v) => setDepthParams(p => ({ ...p, cylinderStrength: v }))} />
                      <button className="sub-toggle" onClick={() => setShowCylinderSub(!showCylinderSub)}>
                        {showCylinderSub ? '▼' : '▶'} Edge Clamp, Gamma, Width & Height
                      </button>
                      {showCylinderSub && (
                        <>
                          <Slider label="Edge Clamp" value={depthParams.cylinderEdgeClamp} min={0.1} max={2.0} step={0.05}
                            onChange={(v) => setDepthParams(p => ({ ...p, cylinderEdgeClamp: v }))} />
                          <Slider label="Cylinder Gamma" value={depthParams.cylinderGamma} min={0.5} max={3.0} step={0.05}
                            onChange={(v) => setDepthParams(p => ({ ...p, cylinderGamma: v }))} />
                          <Slider label="Width Spread" value={depthParams.cylinderWidth} min={0.3} max={2.5} step={0.05}
                            onChange={(v) => setDepthParams(p => ({ ...p, cylinderWidth: v }))} />
                          <Slider label="Height Spread" value={depthParams.cylinderHeight} min={0.3} max={2.5} step={0.05}
                            onChange={(v) => setDepthParams(p => ({ ...p, cylinderHeight: v }))} />
                        </>
                      )}
                    </div>
                    
                    <div style={{ marginTop: 8, marginBottom: 4 }}>
                      <Slider label="Corner‑Plus Emphasis" value={depthParams.cornerPlusStrength} min={-0.5} max={0.5} step={0.01}
                        onChange={(v) => setDepthParams(p => ({ ...p, cornerPlusStrength: v }))} />
                      <button className="sub-toggle" onClick={() => setShowCornerPlusSub(!showCornerPlusSub)}>
                        {showCornerPlusSub ? '▼' : '▶'} Corner Gamma
                      </button>
                      {showCornerPlusSub && (
                        <Slider label="Corner Gamma" value={depthParams.cornerPlusGamma} min={0.5} max={3.0} step={0.05}
                          onChange={(v) => setDepthParams(p => ({ ...p, cornerPlusGamma: v }))} />
                      )}
                    </div>
                    
                    <Slider label="Local Contrast" value={depthParams.localContrast} min={0.0} max={0.5} step={0.02}
                      onChange={(v) => setDepthParams(p => ({ ...p, localContrast: v }))} />
                    <Slider label="Midtone Spread" value={depthParams.midtoneSpread} min={0.0} max={0.8} step={0.05}
                      onChange={(v) => setDepthParams(p => ({ ...p, midtoneSpread: v }))} />
                    <Slider label="Morph Radius" value={depthParams.morphRadius} min={0} max={8} step={1}
                      onChange={(v) => setDepthParams(p => ({ ...p, morphRadius: v }))} />
                    <Slider label="Radial Falloff" value={depthParams.radialFalloff} min={0.0} max={0.5} step={0.02}
                      onChange={(v) => setDepthParams(p => ({ ...p, radialFalloff: v }))} />
                    <Slider label="Noise Reduction" value={depthParams.noiseReduction} min={0.0} max={1.0} step={0.05}
                      onChange={(v) => setDepthParams(p => ({ ...p, noiseReduction: v }))} />
                    <Slider label="Detail Preserve" value={depthParams.detailPreserve} min={0.0} max={1.0} step={0.05}
                      onChange={(v) => setDepthParams(p => ({ ...p, detailPreserve: v }))} />
                  </>
                )}
                
                <button className="regenerate-button" onClick={generateDepthMap} disabled={isProcessing}>
                  {isProcessing ? 'Regenerating...' : '🔄 Regenerate Depth'}
                </button>
              </div>
            )}
            
            {activeTab === 'live' && (
              <div className="live-controls-panel">
                <h3>Live Controls</h3>
                <Slider label="Zoom" value={liveParams.zoom} min={1.0} max={2.0} step={0.01}
                  onChange={(v) => setLiveParams(p => ({ ...p, zoom: v }))} />
                <Slider label="Parallax Strength" value={liveParams.parallaxStrength} min={0.02} max={0.25} step={0.005}
                  onChange={(v) => setLiveParams(p => ({ ...p, parallaxStrength: v }))} />
                <Slider label="Camera Move" value={liveParams.cameraMove} min={0.1} max={0.8} step={0.05}
                  onChange={(v) => setLiveParams(p => ({ ...p, cameraMove: v }))} />
                <Slider label="Background Shift" value={liveParams.backgroundShift} min={0.0} max={0.25} step={0.005}
                  onChange={(v) => setLiveParams(p => ({ ...p, backgroundShift: v }))} />
                <Slider label="Ghost Reduction" value={liveParams.ghostReduction} min={0.0} max={3.0} step={0.05}
                  onChange={(v) => setLiveParams(p => ({ ...p, ghostReduction: v }))} />
                
                <h4 style={{ marginTop: 20, marginBottom: 10 }}>Sensitivity</h4>
                <Slider label="Mouse Sensitivity X" value={liveParams.mouseSensitivityX} min={0.2} max={3.0} step={0.1}
                  onChange={(v) => setLiveParams(p => ({ ...p, mouseSensitivityX: v }))} />
                <Slider label="Mouse Sensitivity Y" value={liveParams.mouseSensitivityY} min={0.2} max={3.0} step={0.1}
                  onChange={(v) => setLiveParams(p => ({ ...p, mouseSensitivityY: v }))} />
                <Slider label="Gyro Sensitivity X" value={liveParams.gyroSensitivityX} min={0.2} max={3.0} step={0.1}
                  onChange={(v) => setLiveParams(p => ({ ...p, gyroSensitivityX: v }))} />
                <Slider label="Gyro Sensitivity Y" value={liveParams.gyroSensitivityY} min={0.2} max={3.0} step={0.1}
                  onChange={(v) => setLiveParams(p => ({ ...p, gyroSensitivityY: v }))} />
              </div>
            )}
            
            <div className="button-group">
              <button className="secondary-button" onClick={() => setStage('generate')}>
                Back
              </button>
              <button className="primary-button" onClick={handleApply}>
                Apply & View 3D
              </button>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        .dynamic-canvas-settings {
          background-color: #2c2c2c;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          width: 100%;
          height: 100%;
        }
        .theme-light .dynamic-canvas-settings {
          background-color: #f5f5f5;
        }
        .dynamic-canvas-settings-header {
          background-color: #595959;
          padding: 20px;
          display: flex;
          align-items: center;
          border-bottom: 1px solid black;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .theme-light .dynamic-canvas-settings-header {
          background-color: #e0e0e0;
          border-bottom-color: #ccc;
        }
        .dynamic-canvas-settings-header h2 {
          color: white;
          margin: 0;
          font-size: 18px;
          font-family: avenir-lt-pro, sans-serif;
          flex: 1;
          text-align: center;
        }
        .theme-light .dynamic-canvas-settings-header h2 {
          color: #333;
        }
        .close-button {
          background: none;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .theme-light .close-button {
          color: #666;
        }
        .dynamic-canvas-settings-content {
          flex: 1;
          padding: 20px;
          max-width: 500px;
          margin: 0 auto;
          width: 100%;
        }
        .generate-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .preview-container {
          background-color: #1a1a1a;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #444;
        }
        .theme-light .preview-container {
          background-color: #fff;
          border-color: #ddd;
        }
        .preview-image {
          width: 100%;
          height: auto;
          display: block;
        }
        .generate-button {
          background-color: #4594f9;
          color: white;
          border: none;
          border-radius: 25px;
          padding: 15px 20px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
        }
        .tuning-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .preview-dual {
          display: flex;
          gap: 10px;
        }
        .preview-dual img {
          width: 50%;
          border-radius: 12px;
          border: 1px solid #444;
          object-fit: cover;
        }
        .theme-light .preview-dual img {
          border-color: #ddd;
        }
        .tab-bar {
          display: flex;
          gap: 5px;
        }
        .tab-button {
          flex: 1;
          background-color: #3a3a5a;
          color: #ccc;
          border: none;
          border-radius: 8px 8px 0 0;
          padding: 10px;
          font-size: 14px;
          cursor: pointer;
        }
        .tab-button.active {
          background-color: #4594f9;
          color: white;
        }
        .depth-tuning-panel, .live-controls-panel {
          background-color: #333333;
          border-radius: 12px;
          padding: 20px;
        }
        .theme-light .depth-tuning-panel, .theme-light .live-controls-panel {
          background-color: #fff;
          border: 1px solid #e0e0e0;
        }
        h3, h4 {
          color: white;
          margin: 0 0 15px 0;
          font-size: 16px;
          text-align: center;
        }
        .theme-light h3, .theme-light h4 {
          color: #333;
        }
        .slider-group {
          margin-bottom: 15px;
        }
        .slider-group label {
          display: block;
          color: #ccc;
          font-size: 12px;
          margin-bottom: 5px;
        }
        .theme-light .slider-group label {
          color: #666;
        }
        .slider-group input {
          width: 100%;
          height: 4px;
          background: #555;
          border-radius: 2px;
          -webkit-appearance: none;
          appearance: none;
        }
        .theme-light .slider-group input {
          background: #ddd;
        }
        .slider-group input::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          background: #4594f9;
          border-radius: 50%;
          cursor: pointer;
        }
        .advanced-toggle, .sub-toggle, .regenerate-button {
          background-color: #3a3a5a;
          color: #ccc;
          border: none;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 12px;
          cursor: pointer;
          width: 100%;
          text-align: left;
          margin: 10px 0;
        }
        .theme-light .advanced-toggle, .theme-light .sub-toggle {
          background-color: #f0f0f0;
          color: #666;
        }
        .regenerate-button {
          background-color: #4594f9;
          color: white;
          text-align: center;
          margin-top: 20px;
        }
        .button-group {
          display: flex;
          gap: 10px;
        }
        .primary-button {
          flex: 1;
          background-color: #4594f9;
          color: white;
          border: none;
          border-radius: 25px;
          padding: 15px 20px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
        }
        .secondary-button {
          flex: 1;
          background-color: #595959;
          color: white;
          border: none;
          border-radius: 25px;
          padding: 15px 20px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
        }
        .theme-light .secondary-button {
          background-color: #e0e0e0;
          color: #666;
        }
        .dynamic-canvas-viewer {
          width: 100%;
          height: 100%;
          position: relative;
        }
        .dynamic-canvas-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .dynamic-canvas-modal-container {
          width: 90%;
          max-width: 500px;
          max-height: 85vh;
          background-color: #2c2c2c;
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .theme-light .dynamic-canvas-modal-container {
          background-color: #ffffff;
        }
      `}</style>
    </div>
  );

  if (isModal) {
    return (
      <div 
        className="dynamic-canvas-modal-overlay" 
        onClick={(e) => {
          // Only close if clicking directly on the overlay background
          if (e.target === e.currentTarget && onDeactivate) {
            onDeactivate();
          }
        }}
      >
        <div className="dynamic-canvas-modal-container" onClick={(e) => e.stopPropagation()}>
          {settingsContent}
        </div>
      </div>
    );
  }
  return settingsContent;
};

export default DynamicCanvas;